import React from 'react'

import { useDispatch } from 'react-redux'

import {
  HowGoustoFoodBoxesWorkPage,
  HowGoustoFoodBoxesWorkCmsPage,
  MealPlanningPage,
  MealPlanningCmsPage,
  FoodBoxPage,
  FoodBoxCmsPage,
  DynamicCmsPage,
} from '@features/marketing-pages'
import {
  fetchCmsBlocks,
  FOOD_BOX_PAGE,
  HOW_GOUSTO_WORKS_PAGE,
  useIsHowGoustoWorksCmsEnabled,
  MEAL_PLANNING_PAGE,
  useIsCmsBlocksEnabled,
  useIsMealPlanningCmsEnabled,
} from '@library/cms-blocks'
import { fetchByUrl } from '@library/contentstack-client'

import { applyPromoCode } from 'actions/home'
import { redirect } from 'actions/redirect'
import { fetchCmsEntry } from 'apis/cms'
import { ErrorPage } from 'components/ErrorPage'
import config from 'config/routes'

export function HowGoustoWorks() {
  const dispatch = useDispatch()
  const isHowGoustoWorksCmsEnabled = useIsHowGoustoWorksCmsEnabled()

  const handleClick = async (path: string) => {
    await dispatch(applyPromoCode())

    dispatch(redirect(path))
  }

  // Control renders static; the Optimizely treatment renders the CMS page.
  return isHowGoustoWorksCmsEnabled ? (
    <HowGoustoFoodBoxesWorkCmsPage startOnboarding={handleClick} cmsFetcher={fetchCmsEntry} />
  ) : (
    <HowGoustoFoodBoxesWorkPage startOnboarding={handleClick} />
  )
}

// Always pre-populate Redux server-side so the CMS variant never flashes in.
HowGoustoWorks.fetchData = ({ store }: { store: { dispatch: (action: unknown) => unknown } }) =>
  store.dispatch(fetchCmsBlocks([HOW_GOUSTO_WORKS_PAGE], fetchByUrl))

export function MealPlanning() {
  const dispatch = useDispatch()
  const isMealPlanningCmsEnabled = useIsMealPlanningCmsEnabled()

  const handleClick = async (path: string) => {
    await dispatch(applyPromoCode())

    dispatch(redirect(path))
  }

  // Control renders static; the Optimizely treatment renders the CMS page.
  return isMealPlanningCmsEnabled ? (
    <MealPlanningCmsPage startOnboarding={handleClick} cmsFetcher={fetchCmsEntry} />
  ) : (
    <MealPlanningPage startOnboarding={handleClick} />
  )
}

// Always pre-populate Redux server-side so the CMS variant never flashes in.
MealPlanning.fetchData = ({ store }: { store: { dispatch: (action: unknown) => unknown } }) =>
  store.dispatch(fetchCmsBlocks([MEAL_PLANNING_PAGE], fetchByUrl))

export function FoodBox() {
  const dispatch = useDispatch()
  const shouldRenderStaticFoodBoxPage = useIsCmsBlocksEnabled()

  const handleClick = async (path: string) => {
    await dispatch(applyPromoCode())

    dispatch(redirect(path))
  }

  // Control renders the CMS page; the Optimizely treatment renders static.
  return shouldRenderStaticFoodBoxPage ? (
    <FoodBoxPage startOnboarding={handleClick} />
  ) : (
    <FoodBoxCmsPage startOnboarding={handleClick} cmsFetcher={fetchCmsEntry} />
  )
}

// Always pre-populate Redux server-side so the CMS variant never flashes in.
FoodBox.fetchData = ({ store }: { store: { dispatch: (action: unknown) => unknown } }) =>
  store.dispatch(fetchCmsBlocks([FOOD_BOX_PAGE], fetchByUrl))

const CMS_PAGE_CONTENT_TYPE = 'cms_page'

type CmsFetchStore = {
  dispatch: (action: unknown) => unknown
  getState: () => {
    cmsBlocks?: Record<string, { error?: boolean; data: unknown } | undefined>
  }
}

// The entry only resolves at fetch time. No entry / fetch failed / null data → throw a 404
// so `renderErrorPages` turns the thrown status into a real HTTP 404 + ErrorPage, with no
// change to processRequest.
function throwIfCmsPageMissing(store: CmsFetchStore, pageUrl: string) {
  const entry = store.getState().cmsBlocks?.[`${CMS_PAGE_CONTENT_TYPE}:${pageUrl}`]
  if (!entry || entry.error || entry.data === null) {
    const notFound: Error & { status?: number } = new Error(`No CMS page published at ${pageUrl}`)
    notFound.status = 404
    throw notFound
  }
}

// Route is `/food-box/*`, so the splat is everything after `/food-box/`.
function toFoodBoxPageUrl(splat: string | undefined): string {
  return `/food-box/${splat ?? ''}`.replace(/\/$/, '')
}

type RouteComponentProps = {
  location?: { pathname?: string }
  params?: { splat?: string }
}

export function FoodBoxCmsCatchAll({ location, params }: RouteComponentProps) {
  const dispatch = useDispatch()

  // Client uses the live pathname; server uses the splat. Same Redux key.
  const pageUrl = location?.pathname ?? toFoodBoxPageUrl(params?.splat)

  const handleTriggerPromocode = async (href: string) => {
    await dispatch(applyPromoCode())

    dispatch(redirect(href))
  }

  return (
    <DynamicCmsPage
      pageUrl={pageUrl}
      cmsFetcher={fetchCmsEntry}
      onTriggerPromocode={handleTriggerPromocode}
      renderNotFound={() => <ErrorPage />}
    />
  )
}

// Resolve the entry by request URL, then throw a 404 on a miss (see throwIfCmsPageMissing).
FoodBoxCmsCatchAll.fetchData = async ({
  params,
  store,
}: {
  params?: { splat?: string }
  store: CmsFetchStore
}) => {
  const pageUrl = toFoodBoxPageUrl(params?.splat)

  await store.dispatch(
    fetchCmsBlocks([{ contentType: CMS_PAGE_CONTENT_TYPE, pageUrl }], fetchByUrl),
  )

  throwIfCmsPageMissing(store, pageUrl)
}

// Each landing page is the generic DynamicCmsPage bound to a fixed CMS URL. The factory
// exists so every route gets its own static `fetchData` — SSR needs to know which entry to
// prime, and an explicit path has no splat to derive it from.
function createCmsLandingPage(pageUrl: string) {
  function LandingPage() {
    const dispatch = useDispatch()

    const handleTriggerPromocode = async (href: string) => {
      await dispatch(applyPromoCode())

      dispatch(redirect(href))
    }

    return (
      <DynamicCmsPage
        pageUrl={pageUrl}
        cmsFetcher={fetchCmsEntry}
        onTriggerPromocode={handleTriggerPromocode}
        renderNotFound={() => <ErrorPage />}
      />
    )
  }

  // SSR: pre-populate Redux for this URL, then 404 on a genuine miss.
  LandingPage.fetchData = async ({ store }: { store: CmsFetchStore }) => {
    await store.dispatch(
      fetchCmsBlocks([{ contentType: CMS_PAGE_CONTENT_TYPE, pageUrl }], fetchByUrl),
    )

    throwIfCmsPageMissing(store, pageUrl)
  }

  return LandingPage
}

export const HeatAndEat = createCmsLandingPage(config.client.landingHeatAndEat)
export const ScratchHeatAndEat = createCmsLandingPage(config.client.landingScratchHeatAndEat)
